import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Plus, Edit, Power, Search, Trash2, AlertTriangle, Mail, ToggleLeft, ToggleRight, Globe } from 'lucide-react';
import InviteClinicModal from '../InviteClinicModal';
import InvitationsManagement from '../InvitationsManagement';
import AddressInput from '../../ui/AddressInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicsManagement({ clinics, pricingTiers, onClinicsChange }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [deletingClinic, setDeletingClinic] = useState(null);
  const [newClinic, setNewClinic] = useState({
    name: '',
    subdomain: '',
    address: '',
    phone: '',
    contact_email: '',
    admin_name: ''
  });
  const [editClinic, setEditClinic] = useState({
    name: '',
    subdomain: '',
    address: '',
    phone: '',
    contact_email: '',
    subscription_tier: '',
    trial_end_date: '',
    is_active: true
  });

  const handleCreateClinic = async () => {
    try {
      // Auto-generate slug from subdomain
      const clinicData = {
        ...newClinic,
        slug: newClinic.subdomain,
        contact_phone: newClinic.phone
      };
      delete clinicData.phone; // Remove phone as we're using contact_phone
      delete clinicData.admin_name; // admin_name not needed in ClinicCreate
      
      await axios.post(`${API_URL}/api/admin/clinics`, clinicData);
      
      setShowCreateDialog(false);
      setNewClinic({ name: '', subdomain: '', address: '', phone: '', contact_email: '', admin_name: '' });
      onClinicsChange();
      toast.success('Clinic created successfully!');
    } catch (error) {
      console.error('Failed to create clinic:', error);
      toast.error(error.response?.data?.detail || 'Failed to create clinic');
    }
  };

  const toggleClinicStatus = async (clinicId, currentIsActive) => {
    try {
      const newIsActive = !currentIsActive;
      await axios.put(`${API_URL}/api/admin/clinics/${clinicId}`, { is_active: newIsActive });
      
      onClinicsChange();
      toast.success(`Clinic ${newIsActive ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Failed to update clinic status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update clinic status');
    }
  };

  const handleEditClinic = (clinic) => {
    setEditingClinic(clinic);
    setEditClinic({
      name: clinic.name,
      subdomain: clinic.subdomain || '',
      address: clinic.address || '',
      phone: clinic.contact_phone || '',
      contact_email: clinic.contact_email || '',
      subscription_tier: clinic.subscription_tier || 'Free Trial',
      trial_end_date: clinic.trial_end_date ? clinic.trial_end_date.split('T')[0] : '',
      is_active: clinic.is_active !== false
    });
    setShowEditDialog(true);
  };

  const handleUpdateClinic = async () => {
    try {
      await axios.put(`${API_URL}/api/admin/clinics/${editingClinic.id}`, editClinic);
      
      setShowEditDialog(false);
      setEditingClinic(null);
      onClinicsChange();
      toast.success('Clinic updated successfully!');
    } catch (error) {
      console.error('Failed to update clinic:', error);
      toast.error(error.response?.data?.detail || 'Failed to update clinic');
    }
  };

  const handleDeleteClinic = (clinic) => {
    setDeletingClinic(clinic);
    setShowDeleteDialog(true);
  };

  const confirmDeleteClinic = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/clinics/${deletingClinic.id}`);
      
      setShowDeleteDialog(false);
      setDeletingClinic(null);
      onClinicsChange();
      toast.success('Clinic deleted successfully!');
    } catch (error) {
      console.error('Failed to delete clinic:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete clinic');
    }
  };

  const filteredClinics = clinics.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.subdomain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Clinic Management</h1>
        <p className="text-gray-600">View and manage all clinics on the platform</p>
      </div>

      <Tabs defaultValue="clinics" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
          <TabsTrigger value="clinics">Clinics</TabsTrigger>
          <TabsTrigger value="invitations">Invitations</TabsTrigger>
        </TabsList>

        <TabsContent value="clinics">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>All Clinics</CardTitle>
                  <CardDescription>{filteredClinics.length} clinic{filteredClinics.length !== 1 ? 's' : ''}</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search clinics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-64"
                    />
                  </div>
                  <Button 
                    variant="outline"
                    onClick={() => setShowInviteModal(true)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Invite Clinic
                  </Button>
                  <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Clinic
                      </Button>
                    </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create New Clinic</DialogTitle>
                    <DialogDescription>Add a new clinic to the platform</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Clinic Name *</label>
                        <Input
                          value={newClinic.name}
                          onChange={(e) => setNewClinic({...newClinic, name: e.target.value})}
                          placeholder="e.g., Wellness Clinic"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Subdomain</label>
                        <Input
                          value={newClinic.subdomain}
                          onChange={(e) => setNewClinic({...newClinic, subdomain: e.target.value})}
                          placeholder="e.g., wellness-clinic"
                        />
                      </div>
                    </div>
                    <div>
                      <AddressInput
                        value={newClinic.address}
                        onChange={(e) => setNewClinic({...newClinic, address: e.target.value})}
                        label="Clinic Address"
                        placeholder="Start typing the clinic address..."
                        multiline={false}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Phone</label>
                      <Input
                        value={newClinic.phone}
                        onChange={(e) => setNewClinic({...newClinic, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Admin Name *</label>
                        <Input
                          value={newClinic.admin_name}
                          onChange={(e) => setNewClinic({...newClinic, admin_name: e.target.value})}
                          placeholder="Admin full name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Contact Email *</label>
                        <Input
                          type="email"
                          value={newClinic.contact_email}
                          onChange={(e) => setNewClinic({...newClinic, contact_email: e.target.value})}
                          placeholder="contact@clinic.com"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateClinic} disabled={!newClinic.name || !newClinic.contact_email || !newClinic.admin_name}>
                      Create Clinic
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredClinics.map((clinic) => (
              <div key={clinic.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{clinic.name}</h3>
                      {clinic.is_active ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Active</span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">Inactive</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Members</p>
                        <p className="font-semibold">{clinic.stats?.total_members || 0} ({clinic.stats?.active_members || 0} active)</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Revenue</p>
                        <p className="font-semibold">${clinic.stats?.total_revenue?.toFixed(2) || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Appointments</p>
                        <p className="font-semibold">{clinic.stats?.total_appointments || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Subscription</p>
                        <div className="flex flex-col gap-1">
                          {(() => {
                            const tier = clinic.subscription_tier || 'Free Trial';
                            const isTrial = tier === 'Free Trial';
                            const trialEndDate = clinic.trial_end_date ? new Date(clinic.trial_end_date) : null;
                            const today = new Date();
                            const daysLeft = trialEndDate ? Math.ceil((trialEndDate - today) / (1000 * 60 * 60 * 24)) : null;
                            
                            return (
                              <>
                                <Badge 
                                  variant={isTrial ? "secondary" : "default"}
                                  className={`w-fit ${isTrial ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'}`}
                                >
                                  {tier}
                                </Badge>
                                {trialEndDate && daysLeft !== null && (
                                  <span className={`text-xs ${daysLeft <= 3 ? 'text-red-600 font-semibold' : daysLeft <= 7 ? 'text-amber-600' : 'text-gray-600'}`}>
                                    {daysLeft > 0 ? `${daysLeft} days left` : 'Trial expired'}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div>
                        <p className="text-gray-500">Subdomain</p>
                        <p className="font-semibold text-teal-600">{clinic.subdomain || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleEditClinic(clinic)}
                      title="Edit Clinic"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => toggleClinicStatus(clinic.id, clinic.is_active)}
                      title={clinic.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <Power className={`h-4 w-4 ${clinic.is_active ? 'text-red-600' : 'text-green-600'}`} />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => handleDeleteClinic(clinic)}
                      title="Delete Clinic"
                      className="hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Clinic Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Clinic</DialogTitle>
            <DialogDescription>Update clinic information and settings</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Activation Status Toggle */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {editClinic.is_active ? (
                    <ToggleRight className="h-6 w-6 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                  )}
                  <div>
                    <label className="text-sm font-semibold">Clinic Activation Status</label>
                    <p className="text-xs text-gray-500">
                      {editClinic.is_active 
                        ? 'Clinic is active and users can log in' 
                        : 'Clinic is deactivated - users cannot log in'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setEditClinic({...editClinic, is_active: !editClinic.is_active})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    editClinic.is_active ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editClinic.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Clinic Name *</label>
                <Input
                  value={editClinic.name}
                  onChange={(e) => setEditClinic({...editClinic, name: e.target.value})}
                  placeholder="e.g., Wellness Clinic"
                />
              </div>
              <div>
                <label className="text-sm font-medium flex items-center gap-1">
                  <Globe className="h-4 w-4 text-teal-600" />
                  Subdomain
                </label>
                <Input
                  value={editClinic.subdomain}
                  onChange={(e) => setEditClinic({...editClinic, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                  placeholder="e.g., wellness-clinic"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase letters, numbers, and hyphens only. Min 3 characters.
                </p>
              </div>
            </div>
            <div>
              <AddressInput
                value={editClinic.address}
                onChange={(e) => setEditClinic({...editClinic, address: e.target.value})}
                label="Clinic Address"
                placeholder="Start typing the clinic address..."
                multiline={false}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={editClinic.phone}
                onChange={(e) => setEditClinic({...editClinic, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Contact Email</label>
              <Input
                type="email"
                value={editClinic.contact_email}
                onChange={(e) => setEditClinic({...editClinic, contact_email: e.target.value})}
                placeholder="contact@clinic.com"
              />
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-semibold mb-3 text-purple-700">Subscription Management</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Subscription Tier</label>
                  <select
                    value={editClinic.subscription_tier}
                    onChange={(e) => setEditClinic({...editClinic, subscription_tier: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="Free Trial">Free Trial</option>
                    {pricingTiers.map(tier => (
                      <option key={tier.slug} value={tier.name}>{tier.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Current subscription plan</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Trial End Date</label>
                  <Input
                    type="date"
                    value={editClinic.trial_end_date}
                    onChange={(e) => setEditClinic({...editClinic, trial_end_date: e.target.value})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty if not on trial</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateClinic} disabled={!editClinic.name || (editClinic.subdomain && editClinic.subdomain.length < 3)}>
              Update Clinic
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Clinic Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Clinic
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the clinic and all associated data.
            </DialogDescription>
          </DialogHeader>
          {deletingClinic && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">
                  {deletingClinic.name}
                </p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• {deletingClinic.stats?.total_members || 0} members will be deleted</p>
                  <p>• {deletingClinic.stats?.total_appointments || 0} appointments will be deleted</p>
                  <p>• ${deletingClinic.stats?.total_revenue?.toFixed(2) || 0} in revenue data will be lost</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Type <strong className="text-red-600">DELETE</strong> to confirm:
              </p>
              <Input
                id="delete-confirmation"
                placeholder="Type DELETE"
                className="mt-2"
              />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={() => {
                const input = document.getElementById('delete-confirmation');
                if (input && input.value === 'DELETE') {
                  confirmDeleteClinic();
                } else {
                  toast.error('Please type DELETE to confirm');
                }
              }}
            >
              Delete Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Clinic Modal */}
      <InviteClinicModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={(data) => {
          console.log('Invitation sent:', data);
          onClinicsChange();
        }}
        pricingTiers={pricingTiers}
      />
        </TabsContent>

        <TabsContent value="invitations">
          <InvitationsManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}
